// src/components/WordCountTextField.js
import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";

import CustomTextFieldWithWordCounter from "./WordCountTextField/CustomTextFieldWithWordCounter";
import TextFieldProgressBar from "./WordCountTextField/TextFieldProgressBar";

/**
 * WordCountTextField
 *
 * Responsibility:
 *  - Manage word counting, validation, and word-limit logic.
 *  - Compute border style + color.
 *  - Call onValidityChange(valid) for parent (e.g. Step 1 / Step 2).
 *  - Delegate rendering to:
 *      - CustomTextFieldWithWordCounter
 *      - TextFieldProgressBar
 *
 * Props:
 *  - value, onChange
 *  - label
 *  - minWords (default 0)
 *  - maxWords (default 100)
 *  - warnOverBy (default 10)   // e.g. 100 + 10 = 110 hard limit
 *  - maxCharsPerWord (default 45)
 *  - onValidityChange (optional callback(valid:boolean))
 *  - placeholder (optional)
 */
export default function WordCountTextField({
  value = "",
  onChange,
  label,
  minWords = 0,
  maxWords = 100,
  warnOverBy = 10,
  maxCharsPerWord = 45,
  onValidityChange,
  minRows,
  ...restProps
}) {
  const [internalValue, setInternalValue] = useState(value);
  const [wordCount, setWordCount] = useState(0);
  const [errorText, setErrorText] = useState("");
  const [dataValid, setDataValid] = useState(true); // for Save button logic
  const [visualError, setVisualError] = useState(false); // for "real" errors

  const hardLimit = maxWords + warnOverBy;

  // 🔁 Sync internal value when parent `value` changes (e.g. fillDummyValues)
  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value || "");
    }
  }, [value, internalValue]);

  // 🧮 Recompute word stats & validity whenever internalValue changes
  useEffect(() => {
    const words = internalValue
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    const count = words.length;
    setWordCount(count);

    let msg = "";
    let valid = true;
    let isVisualError = false;

    // 1) overly long words → always an error (visual)
    const longWords = words.filter((w) => w.length > maxCharsPerWord);
    if (longWords.length > 0) {
      msg = `Some words exceed ${maxCharsPerWord} characters.`;
      valid = false;
      isVisualError = true;
    }

    // 2) upper hard limit → error (visual)
    if (msg === "" && hardLimit > 0 && count >= hardLimit) {
      msg = "Too much — simplify.";
      valid = false;
      isVisualError = true;
    }

    // 3) below minWords → not valid, but not a visual error
    if (msg === "" && count < minWords) {
      valid = false;
      // Guidance text is handled by the progress bar component
    }

    setErrorText(msg);
    setDataValid(valid);
    setVisualError(isVisualError);

    if (onValidityChange) {
      onValidityChange(valid);
    }
  }, [
    internalValue,
    minWords,
    maxWords,
    warnOverBy,
    maxCharsPerWord,
    hardLimit,
    onValidityChange,
  ]);

  // ✍️ Handle user input & block beyond hard word limit
  const handleChange = (e) => {
    const input = e.target.value ?? "";
    const words = input
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (hardLimit > 0 && words.length > hardLimit) {
      return; // hard cap at maxWords + warnOverBy
    }

    setInternalValue(input);
    if (onChange) {
      onChange(input);
    }
  };

  // 🎨 Border style logic (neutral greys, no color meanings)
  let borderStyle = "solid";
  let borderColor = "#bdbdbd"; // neutral grey

  if (wordCount >= minWords && wordCount < maxWords) {
    // within comfortable range
    borderColor = "#212121"; // strong neutral
    borderStyle = "solid";
  } else if (wordCount >= maxWords && wordCount < hardLimit) {
    // near the upper limit
    borderColor = "#212121";
    borderStyle = "dashed";
  } else if (wordCount >= hardLimit) {
    // beyond upper limit
    borderColor = "#212121";
    borderStyle = "dotted";
  }

  return (
    <Box>
      <CustomTextFieldWithWordCounter
        label={label}
        value={internalValue}
        onChange={handleChange}
        wordCount={wordCount}
        hardLimit={hardLimit}
        minRows={minRows || restProps.minRows}
        borderColor={borderColor}
        borderStyle={borderStyle}
        {...restProps}
      />

      <TextFieldProgressBar
        wordCount={wordCount}
        minWords={minWords}
        maxWords={maxWords}
        hardLimit={hardLimit}
        errorText={errorText}
        visualError={visualError}
      />
    </Box>
  );
}
